// src/components/Achievements.js
import React, { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

export const ACHIEVEMENTS = [
  { id:'first_mission',   icon:'👣', name:'First Step',      desc:'Complete your first mission',      check:u=>(u.completedMissions?.length||0)>=1,  reward:25  },
  { id:'five_missions',   icon:'🚀', name:'Getting Started',  desc:'Complete 5 missions',              check:u=>(u.completedMissions?.length||0)>=5,  reward:50  },
  { id:'ten_missions',    icon:'🎯', name:'On a Roll',        desc:'Complete 10 missions',             check:u=>(u.completedMissions?.length||0)>=10, reward:100 },
  { id:'twenty_missions', icon:'⚡', name:'Halfway There',    desc:'Complete 20 missions',             check:u=>(u.completedMissions?.length||0)>=20, reward:150 },
  { id:'all_missions',    icon:'🏆', name:'Completionist',    desc:'Complete all 42 missions',         check:u=>(u.completedMissions?.length||0)>=42, reward:500 },
  { id:'level_5',         icon:'⭐', name:'Rising Star',      desc:'Reach level 5',                    check:u=>(u.level||1)>=5,   reward:100 },
  { id:'level_10',        icon:'🌟', name:'Veteran Coder',    desc:'Reach level 10',                   check:u=>(u.level||1)>=10,  reward:200 },
  { id:'coins_100',       icon:'🪙', name:'Coin Collector',   desc:'Earn 100+ coins',                  check:u=>(u.coins||0)>=100, reward:0   },
  { id:'coins_500',       icon:'💰', name:'High Roller',      desc:'Earn 500+ coins',                  check:u=>(u.coins||0)>=500, reward:0   },
  { id:'coins_1000',      icon:'🐋', name:'Whale',            desc:'Earn 1000+ coins',                 check:u=>(u.coins||0)>=1000,reward:0   },
  { id:'streak_3',        icon:'🔥', name:'Consistent',       desc:'3-day login streak',               check:u=>(u.streak||0)>=3,  reward:30  },
  { id:'streak_7',        icon:'🔥', name:'Week Warrior',     desc:'7-day login streak',               check:u=>(u.streak||0)>=7,  reward:70  },
  { id:'streak_30',       icon:'💎', name:'Iron Will',        desc:'30-day login streak',              check:u=>(u.streak||0)>=30, reward:300 },
  { id:'first_buy',       icon:'🛒', name:'Shopaholic',       desc:'Buy your first shop item',         check:u=>(u.inventory?.length||0)>=1, reward:20 },
  { id:'collector',       icon:'🎁', name:'Collector',        desc:'Own 5+ shop items',                check:u=>(u.inventory?.length||0)>=5, reward:50 },
  { id:'xp_500',          icon:'📈', name:'XP Farmer',        desc:'Earn 500 total XP',                check:u=>(u.xp||0)>=500,    reward:0   },
  { id:'xp_2000',         icon:'📊', name:'XP Master',        desc:'Earn 2000 total XP',               check:u=>(u.xp||0)>=2000,   reward:0   },
  { id:'html_badge',      icon:'🌐', name:'HTML Explorer',    desc:'Earn the HTML Beginner badge',     check:u=>(u.badges||[]).some(b=>b.id==='html_beginner'), reward:50 },
  { id:'css_badge',       icon:'🎨', name:'CSS Explorer',     desc:'Earn the CSS Beginner badge',      check:u=>(u.badges||[]).some(b=>b.id==='css_beginner'),  reward:50 },
  { id:'js_badge',        icon:'⚡', name:'JS Explorer',      desc:'Earn the JS Beginner badge',       check:u=>(u.badges||[]).some(b=>b.id==='js_beginner'),   reward:50 },
];

function AchCard({ a, unlocked }) {
  return (
    <div style={{
      background: unlocked ? 'rgba(255,215,0,0.05)' : 'rgba(13,17,23,0.7)',
      border:`1px solid ${unlocked?'rgba(255,215,0,0.22)':'rgba(255,255,255,0.05)'}`,
      borderRadius:'9px', padding:'14px', display:'flex', alignItems:'center', gap:'12px',
      opacity: unlocked ? 1 : 0.45, filter: unlocked ? 'none' : 'grayscale(0.7)',
      position:'relative', overflow:'hidden',
    }}>
      {unlocked && <div style={{ position:'absolute', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg,#ffd700,transparent)' }} />}
      <span style={{ fontSize:'24px', flexShrink:0 }}>{a.icon}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'2px' }}>
          <span style={{ fontWeight:700, fontSize:'15px', color: unlocked?'#fff':'rgba(255,255,255,0.45)' }}>{a.name}</span>
          {unlocked && <span style={{ fontSize:'15px', color:'#ffd700', fontFamily:'var(--font-game)', letterSpacing:'1px' }}>UNLOCKED</span>}
        </div>
        <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.3)' }}>{a.desc}</div>
      </div>
      {a.reward > 0 && (
        <span style={{ fontSize:'15px', fontWeight:700, color: unlocked?'#ffd700':'rgba(255,255,255,0.18)', flexShrink:0 }}>
          🪙{a.reward}
        </span>
      )}
    </div>
  );
}

export default function Achievements({ compact = false }) {
  const { user } = useAuth();

  const { unlocked, locked, pct } = useMemo(() => {
    if (!user) return { unlocked:[], locked:[], pct:0 };
    const un = ACHIEVEMENTS.filter(a => a.check(user));
    const lk = ACHIEVEMENTS.filter(a => !a.check(user));
    return { unlocked:un, locked:lk, pct:Math.round((un.length/ACHIEVEMENTS.length)*100) };
  }, [user]);

  if (compact) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:'9px' }}>
        <div style={{ flex:1, height:'5px', background:'rgba(255,255,255,0.05)', borderRadius:'3px' }}>
          <div style={{ height:'100%', width:`${pct}%`, background:'#ffd700', borderRadius:'3px' }} />
        </div>
        <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', color:'#ffd700', flexShrink:0 }}>
          {unlocked.length}/{ACHIEVEMENTS.length}
        </span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'18px', padding:'14px 18px',
                    background:'rgba(255,215,0,0.05)', border:'1px solid rgba(255,215,0,0.14)', borderRadius:'10px' }}>
        <div style={{ fontFamily:'var(--font-game)', fontSize:'28px', fontWeight:700, color:'#ffd700' }}>{pct}%</div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, marginBottom:'5px', fontSize:'15px' }}>Achievement Progress</div>
          <div style={{ height:'5px', background:'rgba(255,255,255,0.05)', borderRadius:'3px' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,#ffd700,#ff8c00)', borderRadius:'3px', transition:'width 0.6s' }} />
          </div>
          <div style={{ marginTop:'4px', fontSize:'15px', color:'rgba(255,255,255,0.3)' }}>
            {unlocked.length} of {ACHIEVEMENTS.length} unlocked
          </div>
        </div>
      </div>

      {unlocked.length > 0 && (
        <div style={{ marginBottom:'20px' }}>
          <div className="section-title">UNLOCKED ({unlocked.length})</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'9px' }}>
            {unlocked.map(a => <AchCard key={a.id} a={a} unlocked />)}
          </div>
        </div>
      )}

      {locked.length > 0 && (
        <div>
          <div className="section-title">LOCKED ({locked.length})</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'9px' }}>
            {locked.map(a => <AchCard key={a.id} a={a} unlocked={false} />)}
          </div>
        </div>
      )}
    </div>
  );
}
